/** Website 2.7.000: audience-led marketing. Availability is explicit; no invented activity or earnings. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  ShoppingBag,
  Store,
  Truck,
  ClipboardCheck,
  Play,
  Music2,
  Gamepad2,
  MapPin,
  Clock3,
  SlidersHorizontal,
  Check,
  ChevronRight,
} from "lucide-react";
import LeadCapture from "../components/LeadCapture";
import { BuyerPhone } from "./MarketingExperience";
import { gameCatalog } from "./audienceContent";
import "./ConnectedHome.css";

export function Action({ to, children, secondary = false, role }) {
  return (
    <Link
      className={`cw-action${secondary ? " cw-secondary" : ""}`}
      to={to}
      state={role ? { role } : undefined}
    >
      {children}
      <ArrowUpRight size={19} aria-hidden="true" />
    </Link>
  );
}
const roles = [
  {
    id: "buyers",
    icon: ShoppingBag,
    title: "A little less errand.",
    text: "Groceries, local finds and everyday essentials. Bring your list together and make room for your day.",
    action: "Discover shopping",
    color: "peach",
  },
  {
    id: "sellers",
    icon: Store,
    title: "Your next regular.",
    text: "Bring your shelves online. Manage orders, connect with customers and give them a reason to return.",
    action: "Grow with KwK",
    color: "sage",
  },
  {
    id: "drivers",
    icon: Truck,
    title: "Work on your terms.",
    text: "Set your delivery rates, choose when you’re available and review the offer before you accept.",
    action: "Explore driving",
    color: "yellow",
  },
  {
    id: "pickers",
    icon: ClipboardCheck,
    title: "A good eye goes far.",
    text: "Turn thoughtful shopping into an opportunity. Pick with care, handle substitutions and keep buyers in the loop.",
    action: "Become a picker",
    color: "lavender",
  },
];
const controlDetails = [
  {
    name: "Your rates",
    icon: SlidersHorizontal,
    title: "Put a value on your time.",
    body: "Configure your delivery pricing in the app. Review the job’s pay, distance and details before deciding whether it works for you.",
    bullets: [
      "Pricing settings you can manage",
      "An offer to review before accepting",
      "Clearer decisions about your next trip",
    ],
  },
  {
    name: "Your hours",
    icon: Clock3,
    title: "Build work around your day.",
    body: "Choose your availability and manage your schedule. Start with the time you want to give, then explore the work available in your area.",
    bullets: [
      "Your availability, in your hands",
      "Schedules and daily goals together",
      "Local demand and eligibility apply",
    ],
  },
  {
    name: "Your choices",
    icon: Truck,
    title: "The offer comes before the yes.",
    body: "Look at compensation, estimated time and distance. Choose the jobs that fit, and keep mileage, expenses and settlement details in view.",
    bullets: [
      "Review pay and trip details",
      "Choose whether to accept",
      "Track the work you complete",
    ],
  },
];
function DriverControl() {
  const [selected, setSelected] = useState(0);
  const d = controlDetails[selected];
  return (
    <section className="cw-work cw-wrap" aria-labelledby="work-title">
      <div>
        <span className="cw-kicker">INDEPENDENCE LOOKS GOOD ON YOU</span>
        <h2 id="work-title">
          Your work.
          <br />
          Your call.
        </h2>
        <p>
          You’re behind the wheel. You should be part of the decision. KwK puts
          your pricing, availability and job choices within reach.
        </p>
        <Action to="/drivers">Make your next move</Action>
        <p className="cw-note">
          Work availability varies. Rates and offers follow the applicable
          service rules; earnings are not guaranteed.
        </p>
      </div>
      <div className="cw-controls">
        <div className="cw-control-top">
          <span>THE DRIVER’S SEAT</span>
          <Truck size={27} />
        </div>
        <div
          className="cw-control-tabs"
          role="group"
          aria-label="Explore driver control"
        >
          {controlDetails.map((x, i) => (
            <button
              key={x.name}
              onClick={() => setSelected(i)}
              aria-pressed={i === selected}
            >
              <x.icon size={18} />
              {x.name}
            </button>
          ))}
        </div>
        <div className="cw-control-copy" aria-live="polite">
          <span className="cw-large-number">0{selected + 1}</span>
          <h3>{d.title}</h3>
          <p>{d.body}</p>
          <ul>
            {d.bullets.map((t) => (
              <li key={t}>
                <Check size={17} />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <Link to="/drivers#fair-pay" className="cw-control-link">
          Explore driver tools <ArrowUpRight size={18} />
        </Link>
      </div>
    </section>
  );
}
// On a media detail page, its own card advances to useful access information
// instead of sending the visitor back to the page they are already reading.
export function MediaCards({ currentKind } = {}) {
  return (
    <div className="cw-media-grid">
      <Link
        to={currentKind === "tube" ? "/tube#media-faq" : "/tube"}
        className="cw-media-card cw-tube"
      >
        <div className="cw-media-visual" aria-hidden="true">
          <span className="cw-film-frame">
            <Play size={60} fill="currentColor" strokeWidth={0} />
          </span>
          <span className="cw-film-caption">A fresh perspective.</span>
        </div>
        <div className="cw-media-copy">
          <span className="cw-badge">CREATOR PILOT</span>
          <h3>
            KwK Tube <ArrowUpRight />
          </h3>
          <p>A place for video, fresh ideas and the people who make them.</p>
          <span>
            {currentKind === "tube"
              ? "See access and creator FAQs"
              : "Discover KwK Tube"}{" "}
            <ArrowRight size={16} />
          </span>
        </div>
      </Link>
      <Link
        to={currentKind === "tunes" ? "/tunes#media-faq" : "/tunes"}
        className="cw-media-card cw-tunes"
      >
        <div className="cw-media-visual" aria-hidden="true">
          <div className="cw-record">
            <i />
            <span>
              kwk
              <br />
              <small>tunes</small>
            </span>
          </div>
          <div className="cw-soundbars">
            {Array.from({ length: 18 }, (_, i) => (
              <i key={i} style={{ "--bar": `${20 + ((i * 31) % 80)}%` }} />
            ))}
          </div>
        </div>
        <div className="cw-media-copy">
          <span className="cw-badge">CREATOR PILOT</span>
          <h3>
            KwK Tunes <ArrowUpRight />
          </h3>
          <p>Music and audio for the moments between everything else.</p>
          <span>
            {currentKind === "tunes"
              ? "See access and creator FAQs"
              : "Discover KwK Tunes"}{" "}
            <ArrowRight size={16} />
          </span>
        </div>
      </Link>
    </div>
  );
}
export default function ConnectedHome() {
  useEffect(() => {
    document.title = "KwK | Shop. Work. Play. Make it your day.";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Local shopping, independent delivery, seller tools and a little more joy. Discover KwK for buyers, sellers, drivers and pickers, plus KwK Play, Tube and Tunes.",
      );
  }, []);
  return (
    <main id="main-content" className="cw-site">
      <section className="cw-hero cw-wrap">
        <div className="cw-hero-copy">
          <span className="cw-kicker">
            <span className="cw-orange-dot" />
            ONE APP. MORE OF YOUR DAY.
          </span>
          <h1>
            Life happens.
            <br />
            Make it <em>KwK.</em>
          </h1>
          <p>
            Get your essentials. Find your next opportunity. Make time for a
            little fun. A whole neighborhood of possibilities, together in one
            app.
          </p>
          <div className="cw-actions">
            <Action to="/buyers">Explore the app</Action>
            <Action to="/#ecosystem" secondary>
              Find your place
            </Action>
          </div>
          <div className="cw-hero-foot">
            <MapPin size={16} />
            <span>Growing neighborhood by neighborhood.</span>
          </div>
        </div>
        <div className="cw-hero-picture">
          <img
            src="/campaign/neighborhood-2.7.000.jpg"
            alt="Illustration of a neighborhood shopkeeper handing fresh groceries to a delivery courier"
            width="1536"
            height="1024"
            fetchPriority="high"
          />
          <div className="cw-photo-tag">
            <span className="cw-tag-icon">
              <ShoppingBag />
            </span>
            <div>
              <small>GOOD THINGS, CLOSER.</small>
              <strong>Your neighborhood. Connected.</strong>
            </div>
          </div>
          <span className="cw-image-credit">
            Original campaign illustration
          </span>
        </div>
      </section>
      <div className="cw-ticker" aria-label="KwK experiences">
        <span>Shop local</span>
        <i>✳</i>
        <span>Choose your work</span>
        <i>✳</i>
        <span>Grow your business</span>
        <i>✳</i>
        <span>Press play</span>
        <i>✳</i>
        <span>Make it yours</span>
      </div>
      <section id="ecosystem" className="cw-section cw-wrap">
        <div className="cw-section-head">
          <div>
            <span className="cw-kicker">A PLACE FOR YOUR KIND OF DAY</span>
            <h2>
              However you show up,
              <br />
              there’s a place for you.
            </h2>
          </div>
          <p>One connected app. Different ways to make it work for you.</p>
        </div>
        <div className="cw-role-grid">
          {roles.map((r) => (
            <Link
              to={"/" + r.id}
              key={r.id}
              className={`cw-role cw-${r.color}`}
            >
              <span className="cw-role-head">
                <r.icon size={27} />
                <span>{r.id}</span>
                <ArrowUpRight size={20} />
              </span>
              <h3>{r.title}</h3>
              <p>{r.text}</p>
              <span className="cw-role-cta">
                {r.action}
                <ArrowRight size={18} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <section id="launcher" className="cw-launcher">
        <div className="cw-wrap cw-launcher-grid">
          <div className="cw-phone-stage">
            <BuyerPhone />
          </div>
          <div>
            <span className="cw-kicker">LESS APP-HOPPING. MORE LIVING.</span>
            <h2>
              Big on possibilities.
              <br />
              Built around you.
            </h2>
            <p>
              Your essentials don’t live in separate lives. Why should your
              apps? Keep shopping, orders, conversations and play close—with a
              home you can arrange your way.
            </p>
            <div className="cw-feature-lines">
              <div>
                <span>01</span>
                <div>
                  <h3>Your home, your way</h3>
                  <p>Arrange applets, folders, widgets and wallpaper.</p>
                </div>
              </div>
              <div>
                <span>02</span>
                <div>
                  <h3>From your list to your doorstep</h3>
                  <p>
                    Review your cart, choose replacements and follow your order.
                  </p>
                </div>
              </div>
              <div>
                <span>03</span>
                <div>
                  <h3>More than one side of you</h3>
                  <p>Switch between your available roles in the same app.</p>
                </div>
              </div>
            </div>
            <Action to="/buyers">Meet your everyday app</Action>
          </div>
        </div>
      </section>
      <DriverControl />
      <section className="cw-seller cw-wrap">
        <div className="cw-seller-message">
          <span className="cw-kicker">FOR THE SHOP AROUND THE CORNER</span>
          <h2>
            Be someone’s
            <br />
            new favorite.
          </h2>
          <p>
            A storefront is just the start. Bring your products, orders and
            customer relationships together. Create offers that give people a
            reason to come back.
          </p>
          <Action to="/sellers">Bring your business to KwK</Action>
        </div>
        <div className="cw-seller-path">
          <span className="cw-seller-label">
            FROM DISCOVERY TO THE NEXT VISIT
          </span>
          {[
            [
              "Get discovered",
              "Put your products and local business in reach.",
            ],
            [
              "Deliver your best",
              "Manage stock, store hours and incoming orders.",
            ],
            [
              "Give them a reason to return",
              "Explore coupons, seller offers and eligible sponsored play.",
            ],
          ].map(([t, b], i) => (
            <div key={t}>
              <span>{i + 1}</span>
              <article>
                <h3>{t}</h3>
                <p>{b}</p>
              </article>
            </div>
          ))}
          <Link to="/game-promotions">
            Explore game promotions <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
      <section className="cw-entertainment">
        <div className="cw-wrap">
          <div className="cw-section-head">
            <div>
              <span className="cw-kicker">STAY FOR THE GOOD PART</span>
              <h2>
                Errands done.
                <br />
                Curiosity on.
              </h2>
            </div>
            <p>
              Discover what else is taking shape in KwK. Games to get into.
              Video to explore. Audio for your day.
            </p>
          </div>
          <MediaCards />
          <p className="cw-note">
            Tube and Tunes are in a controlled creator rollout. Catalogs, access
            and features depend on availability. These illustrations are not
            live media or playback controls.
          </p>
          <div className="cw-play-heading">
            <div>
              <span className="cw-kicker">KWK PLAY</span>
              <h3>Your next “one more level.”</h3>
            </div>
            <Action to="/play" secondary>
              Explore all games
            </Action>
          </div>
          <div className="cw-games-grid">
            {gameCatalog.slice(0, 4).map((g) => (
              <Link key={g.id} to={"/play/" + g.id} className="cw-game">
                <div>
                  <img
                    src={"/game-art/" + g.id + ".jpg"}
                    width="640"
                    height="640"
                    loading="lazy"
                    alt={`${g.name} original key artwork`}
                  />
                  <span>
                    <Gamepad2 size={17} /> {g.genre}
                  </span>
                </div>
                <h4>
                  {g.name}
                  <ArrowUpRight size={17} />
                </h4>
              </Link>
            ))}
          </div>
          <p className="cw-note">
            Download the games you choose. Play privately. Game scores and
            power-ups are separate from eligible sponsor-funded rewards.
            Promotional artwork is not a gameplay screenshot.
          </p>
        </div>
      </section>
      <section className="cw-wrap cw-rewards" id="rewards">
        <div>
          <span className="cw-kicker">REASONS TO COME BACK</span>
          <h2>
            A good find.
            <br />A little extra.
          </h2>
        </div>
        <div>
          <p>
            Discover participating sellers’ coupons and offers. See what a
            reward is for, who funds it and when it expires—before you take
            part.
          </p>
          <Action secondary to="/rewards">
            Understand KwK rewards
          </Action>
          <p className="cw-note">
            The expanded points program is rolling out. Eligibility and offer
            terms apply. Points are not cash; no cash-out.
          </p>
        </div>
      </section>
      <section id="service-area" className="cw-service">
        <div className="cw-wrap">
          <MapPin size={28} />
          <div>
            <h2>Let’s make your neighborhood KwK.</h2>
            <p>
              Shopping, services and work depend on your area. Tell us where
              you’d like to get started.
            </p>
          </div>
          <Action to="/#join" role="buyer">
            Connect with KwK
          </Action>
        </div>
      </section>
      <LeadCapture />
    </main>
  );
}
